import { showImperativeToast } from '../providers/UIProvider';
import { shouldSuppressAuthReauthPlayToast } from '../lib/authHealth';
import { wasTransportRelayLastResortUsed } from '../lib/ipcTransportDegraded';
import { hasRecentIsolationFailure, rewritePlayErrorIfProxyFetchBlocked, hasRecentTransportPlayFailure } from './proxyFetchFailedUI';
import { isDeepBufferTimerRenderer } from '../transport/types';
import { useMaestroStore } from '../store/maestroStore';

const TOAST_MS = 12_000;
/** Let Connector `proxy_fetch_failed` land before generic retry copy on network renderers. */
const NETWORK_RETRY_TOAST_DEFER_MS = 45;

let pendingUserPlayRetryToast: ReturnType<typeof setTimeout> | null = null;

/** Human-readable stream format from track metadata or Qobuz format_id. */
export function formatStreamDetail(
  track?: {
    format?: string;
    sampleRate?: number;
    sample_rate?: number;
    bitDepth?: number;
    bit_depth?: number;
  } | null,
  formatId?: number | null,
): string {
  const srRaw = Number(track?.sampleRate || track?.sample_rate || 0);
  const bd = Number(track?.bitDepth || track?.bit_depth || 0);
  const fmt = String(track?.format || '').trim();

  if (bd > 0 && srRaw > 0) {
    const khz = srRaw >= 1000 ? srRaw / 1000 : srRaw;
    const khzLabel = Number.isInteger(khz) ? String(khz) : khz.toFixed(1);
    return `${bd}-bit / ${khzLabel} kHz`;
  }
  if (fmt) return fmt;
  if (formatId === 27) return 'hi-res lossless';
  if (formatId === 6) return 'CD quality (16-bit / 44.1 kHz)';
  if (formatId === 7) return 'lossy (MP3/AAC)';
  return 'this format';
}

function isSoapPlayAfterSetUriFailure(errMsg: string): boolean {
  const lower = errMsg.toLowerCase();
  if (lower.includes('play failed') && (lower.includes('soap error 500') || lower.includes('http 500'))) {
    return true;
  }
  if (lower.includes('setavtransporturi ok') && lower.includes('play')) {
    return true;
  }
  return false;
}

function isBlanketRetryEngineMessage(errMsg: string): boolean {
  const lower = errMsg.toLowerCase();
  return (
    lower.includes('different connection methods')
    || (lower.includes('rejected the audio stream after') && lower.includes('attempt'))
    || (lower.includes('investigate compatibility') && lower.includes('devices'))
    || (lower.includes('logged') && lower.includes('compatibility'))
  );
}

const RELAY_UNREGISTERED_RE =
  /relay token is not registered|invalid_relay_token|cloud relay not ready/i;

const CLOUD_RELAY_GUIDANCE =
  "Maestro can't reach its cloud service — your network or security software may be blocking api.maestroaudio.net";

/**
 * Replace raw relay-registration errors with plain network guidance (#248 B5).
 * Only applies when tier-5 cloud relay was the legitimate last resort.
 */
export function rewriteRelayLastResortError(errMsg: string): string {
  if (!errMsg || !wasTransportRelayLastResortUsed()) return errMsg;
  if (RELAY_UNREGISTERED_RE.test(errMsg)) {
    return CLOUD_RELAY_GUIDANCE;
  }
  return errMsg;
}

/**
 * Replace misleading Connector retry-engine copy (e.g. "tried 4 connection methods")
 * with accurate playback failure guidance, including format/sample-rate when known.
 */
export function rewriteNetworkPlayError(
  errMsg: string,
  track?: {
    title?: string;
    format?: string;
    sampleRate?: number;
    sample_rate?: number;
    bitDepth?: number;
    bit_depth?: number;
  } | null,
  formatId?: number | null,
  rendererName?: string | null,
): string {
  if (!errMsg) return errMsg;

  const relayRewritten = rewriteRelayLastResortError(errMsg);
  if (relayRewritten !== errMsg) return relayRewritten;

  const device = (rendererName || 'Your device').trim();
  const title = (track?.title || 'this track').trim();
  const detail = formatStreamDetail(track, formatId);

  if (isSoapPlayAfterSetUriFailure(errMsg) || isBlanketRetryEngineMessage(errMsg)) {
    return `${device} couldn't play "${title}" (${detail}). Try another track or a lower quality setting.`;
  }

  if (errMsg.toLowerCase().includes('rejected the audio stream')
    || errMsg.toLowerCase().includes('rejected stream')) {
    return `${device} couldn't play "${title}" (${detail}). The stream format may not be supported.`;
  }

  return errMsg;
}

/** Pause-and-retry toast for a user-initiated play failure (no auto-advance). */
export function showUserPlayRetryToast(
  trackTitle: string,
  rendererName: string,
  errMsg?: string | null,
  track?: {
    title?: string;
    format?: string;
    sampleRate?: number;
    sample_rate?: number;
    bitDepth?: number;
    bit_depth?: number;
  } | null,
  formatId?: number | null,
  rendererId?: string | null,
): void {
  const show = () => {
    if (shouldSuppressAuthReauthPlayToast(errMsg)) {
      return;
    }
    if (hasRecentIsolationFailure(rendererId, rendererName)) {
      return;
    }

    const title = trackTitle || track?.title || 'This track';
    const device = rendererName || 'your device';

    if (!errMsg && rendererId) {
      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      const zone =
        store.discovery?.activeZone?.id === renderer?.id
          ? store.discovery.activeZone
          : store.discovery?.discoveredZones?.find((z) => z.id === renderer?.id) ?? store.discovery?.activeZone;
      if (
        isDeepBufferTimerRenderer(renderer, zone)
        && !hasRecentTransportPlayFailure(rendererId)
      ) {
        return;
      }
    }

    let message: string;

    if (errMsg) {
      const rewritten = rewriteNetworkPlayError(errMsg, track || { title }, formatId, device);
      const proxyRewritten = rewritePlayErrorIfProxyFetchBlocked(rewritten, rendererId, device);
      if (hasRecentIsolationFailure(rendererId, device)) {
        return;
      }
      if (proxyRewritten !== errMsg && proxyRewritten !== rewritten) {
        message = `${proxyRewritten} Press play to try again.`;
      } else if (rewritten !== errMsg) {
        message = `${rewritten} Press play to try again.`;
      } else {
        message = `Couldn't play "${title}" on ${device}. Press play to try again.`;
      }
    } else {
      const detail = formatStreamDetail(track, formatId);
      message = `${device} couldn't play "${title}" (${detail}). Press play to try again.`;
    }

    showImperativeToast(message, TOAST_MS);
  };

  if (rendererId) {
    if (pendingUserPlayRetryToast) clearTimeout(pendingUserPlayRetryToast);
    pendingUserPlayRetryToast = setTimeout(() => {
      pendingUserPlayRetryToast = null;
      show();
    }, NETWORK_RETRY_TOAST_DEFER_MS);
    return;
  }

  show();
}

/** Network play failure toast with error rewrite chain. */
export function showNetworkPlayFailureToast(
  errMsg: string,
  rendererId?: string | null,
  rendererName?: string | null,
  track?: {
    title?: string;
    format?: string;
    sampleRate?: number;
    sample_rate?: number;
    bitDepth?: number;
    bit_depth?: number;
  } | null,
  formatId?: number | null,
  durationMs = TOAST_MS,
): void {
  if (shouldSuppressAuthReauthPlayToast(errMsg)) {
    return;
  }
  if (hasRecentIsolationFailure(rendererId, rendererName)) {
    return;
  }

  const rewritten = rewriteNetworkPlayError(errMsg, track, formatId, rendererName);
  const message = rewritePlayErrorIfProxyFetchBlocked(rewritten, rendererId, rendererName);
  showImperativeToast(message, durationMs);
}
